import { useRef } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import {
    getMatchedIntroOpenAtom,
    getMatchedModalOpenAtom,
    useCloseBelowDesktop,
} from '~/state/ui-state-get-matched-modal';

const INTRO_EYEBROW = 'Get Matched';

/** Rendered as two lines so the break lands on the sentence, not mid-clause. */
const INTRO_HEADING_LEAD = 'Describe it once.';
const INTRO_HEADING_EMPHASIS = "We'll do the matching.";

const INTRO_BODY =
    "Answer a few questions about what you're after — category, location, budget and timeline. Our team reviews your brief and personally matches you with 2–3 professionals, projects or products that fit. It takes about two minutes.";

/**
 * Interim card shown between a Get Matched trigger and the brief flow while the
 * feature rolls out: it names the feature and sets expectations before the
 * multi-step brief opens. Continue hands over to `GetMatchedModal`; closing
 * dismisses without entering the flow.
 *
 * Mounted by `GetMatchedModal` so every existing trigger picks it up, and held
 * to the same `md` breakpoint as the brief. `useOpenGetMatchedModal` refuses to
 * open below that breakpoint, so this card never mounts as a bare overlay.
 *
 * The overlay is deliberately left at the shared `DialogOverlay` default so the
 * scrim doesn't change between this card and the brief it hands over to.
 */
export const GetMatchedIntroModal = () => {
    const [open, setOpen] = useAtom(getMatchedIntroOpenAtom);
    const setBriefOpen = useSetAtom(getMatchedModalOpenAtom);

    /**
     * Set for the Continue path only, so the close handler below can tell a
     * handover from an ordinary dismiss.
     */
    const handingOverRef = useRef(false);

    useCloseBelowDesktop(open, setOpen);

    const handleContinue = () => {
        handingOverRef.current = true;
        setOpen(false);
        setBriefOpen(true);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                spacing="none"
                // The design's close control is a filled dark disc with the
                // light "times" glyph, which the shared close button can't
                // express — rendered below against the dialog's own state.
                closeButton={false}
                onCloseAutoFocus={(event) => {
                    // Dismissing leaves Radix to restore focus to whatever
                    // held it before the card opened, which is what should
                    // happen when nothing takes over.
                    if (!handingOverRef.current) return;
                    // On Continue the brief is opening as this one closes, and
                    // it mounts first. Restoring focus to the trigger would
                    // pull the user out of the brief they just entered and
                    // behind its overlay, so the restore is suppressed.
                    handingOverRef.current = false;
                    event.preventDefault();
                }}
                // `sm:rounded-[2.25rem]` restates the radius under the `sm:`
                // prefix so it replaces the base variant's `sm:rounded-lg` —
                // an unprefixed radius can't override a prefixed one.
                className="hidden w-[56.75rem] max-w-[calc(100vw-3rem)] gap-0 overflow-hidden rounded-[2.25rem] border-0 bg-white px-[5.2808rem] pt-[5.4035rem] pb-[5.4715rem] shadow-2xl sm:rounded-[2.25rem] md:grid"
            >
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    className="absolute top-[1.4286rem] right-[1.3456rem] flex size-[2.6077rem] items-center justify-center rounded-full bg-[#141414] text-white transition-colors hover:bg-black"
                >
                    <svg
                        viewBox="0 0 23.6048 23.6048"
                        fill="none"
                        aria-hidden="true"
                        className="size-[1.4753rem]"
                    >
                        <path
                            d="M13.4718 11.8027L18.5155 6.75893L19.5556 5.71881C19.709 5.56538 19.709 5.31604 19.5556 5.16261L18.4427 4.0497C18.2893 3.89627 18.0399 3.89627 17.8865 4.0497L11.8027 10.1335L5.71881 4.04921C5.56538 3.89578 5.31604 3.89578 5.16261 4.04921L4.04921 5.16212C3.89578 5.31555 3.89578 5.56489 4.04921 5.71832L10.1335 11.8027L4.04921 17.8865C3.89578 18.0399 3.89578 18.2893 4.04921 18.4427L5.16212 19.5556C5.31555 19.709 5.56489 19.709 5.71832 19.5556L11.8027 13.4718L16.8464 18.5155L17.8865 19.5556C18.0399 19.709 18.2893 19.709 18.4427 19.5556L19.5556 18.4427C19.709 18.2893 19.709 18.0399 19.5556 17.8865L13.4718 11.8027Z"
                            fill="currentColor"
                        />
                    </svg>
                </button>
                <div className="flex w-full max-w-[42.1729rem] flex-col items-start gap-[2.5rem]">
                    <div className="flex w-full max-w-[39.439rem] flex-col gap-[0.875rem]">
                        <p className="font-sans text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.046875rem] text-[#9a9a9a]">
                            {INTRO_EYEBROW}
                        </p>
                        <DialogTitle className="font-sans text-[3.625rem] leading-[1.1] font-medium tracking-[-0.0725rem] text-[#141414]">
                            <span className="block">{INTRO_HEADING_LEAD}</span>
                            <span className="block">
                                {INTRO_HEADING_EMPHASIS}
                            </span>
                        </DialogTitle>
                    </div>
                    <DialogDescription className="font-sans text-[1.19375rem] leading-[1.58] font-normal tracking-[-0.0298rem] text-[#141414]">
                        {INTRO_BODY}
                    </DialogDescription>
                    <button
                        type="button"
                        onClick={handleContinue}
                        className="flex h-[3.25rem] w-[11.625rem] items-center justify-center rounded-[1.875rem] bg-[#141414] px-[1.625rem] py-3 text-[1rem] leading-[1.3] font-medium tracking-[0.02rem] text-white transition-colors hover:bg-black"
                    >
                        Continue
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
